import React, { useState } from 'react';
import { X, UserPlus, UserMinus, Edit2, Check } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { apiPatch, apiPost, apiDelete } from '../../utils/api';
import UserSearchSelect from './UserSearchSelect';

export default function ConversationSettings({ conversation, onClose, onUpdated }) {
    const { theme } = useTheme();
    const { currentUser } = useAuth();
    const [editingTitle, setEditingTitle] = useState(false);
    const [title, setTitle] = useState(conversation.title || '');
    const [showAddMember, setShowAddMember] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [loading, setLoading] = useState(false);

    const isAdmin = conversation.members?.find(m => m.id === currentUser?.id)?.role === 'admin';
    const isGroup = conversation.conversation_type === 'group';

    const handleUpdateTitle = async () => {
        if (!title.trim() || title.trim() === conversation.title) {
            setEditingTitle(false);
            return;
        }

        try {
            const response = await apiPatch(`/conversations/${conversation.id}`, { title: title.trim() });
            if (response.ok) {
                const data = await response.json();
                onUpdated(data.conversation);
                setEditingTitle(false);
            }
        } catch (err) {
            console.error('Failed to update title:', err);
        }
    };

    const handleAddMembers = async () => {
        if (selectedUsers.length === 0) return;
        setLoading(true);

        try {
            const response = await apiPost(`/conversations/${conversation.id}/members`, {
                user_ids: selectedUsers.map(u => u.id)
            });
            if (response.ok) {
                const data = await response.json();
                onUpdated(data.conversation);
                setSelectedUsers([]);
                setShowAddMember(false);
            }
        } catch (err) {
            console.error('Failed to add members:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveMember = async (userPublicId) => {
        if (!window.confirm('Remove this member from the group?')) return;

        try {
            const response = await apiDelete(`/conversations/${conversation.id}/members/${userPublicId}`);
            if (response.ok) {
                onUpdated({
                    ...conversation,
                    members: conversation.members.filter(m => m.id !== userPublicId)
                });
            }
        } catch (err) {
            console.error('Failed to remove member:', err);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
             onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div className={`w-full max-w-sm rounded-lg shadow-xl flex flex-col max-h-[70vh] overflow-hidden ${
            theme === 'dark' ? 'bg-gray-800' : 'bg-white'
        }`}>
            <div className={`flex items-center justify-between px-4 py-3 border-b flex-shrink-0 ${
                theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}>
                <h3 className="font-dm-serif font-semibold text-sm">Details</h3>
                <button onClick={onClose} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700">
                    <X size={16} />
                </button>
            </div>

            <div className="p-4 space-y-4 overflow-y-auto flex-1">
                {/* Title section (group only) */}
                {isGroup && (
                    <div>
                        <label className={`block text-xs font-rasa font-medium mb-1 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>Group Name</label>
                        {editingTitle && isAdmin ? (
                            <div className="flex items-center gap-2">
                                <input
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleUpdateTitle()}
                                    className={`flex-1 px-2 py-1 rounded text-sm font-rasa border ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600 text-white'
                                            : 'bg-white border-gray-300 text-gray-900'
                                    } focus:outline-none focus:ring-1 focus:ring-blue-500`}
                                    autoFocus
                                />
                                <button onClick={handleUpdateTitle} className="p-1 text-blue-500 hover:text-blue-600">
                                    <Check size={14} />
                                </button>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2">
                                <p className="text-sm font-unna">{conversation.title}</p>
                                {isAdmin && (
                                    <button
                                        onClick={() => setEditingTitle(true)}
                                        className={`p-1 rounded ${
                                            theme === 'dark' ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                                        }`}
                                    >
                                        <Edit2 size={12} />
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Members */}
                <div>
                    <div className="flex items-center justify-between mb-2">
                        <label className={`block text-xs font-rasa font-medium ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>Members ({conversation.members?.length || 0})</label>
                        {isAdmin && (
                            <button
                                onClick={() => setShowAddMember(!showAddMember)}
                                className="p-1 rounded text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                                title={isGroup ? 'Add members' : 'Add member (converts to group)'}
                            >
                                <UserPlus size={14} />
                            </button>
                        )}
                    </div>

                    {showAddMember && (
                        <div className="mb-3">
                            {!isGroup && (
                                <p className={`text-xs font-rasa mb-2 ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                                }`}>
                                    Adding a member will convert this to a group chat.
                                </p>
                            )}
                            <UserSearchSelect
                                selectedUsers={selectedUsers}
                                context="group"
                                onSelect={(user) => setSelectedUsers(prev => [...prev, user])}
                                onRemove={(userId) => setSelectedUsers(prev => prev.filter(u => u.id !== userId))}
                                excludeIds={conversation.members?.map(m => m.id) || []}
                            />
                            {selectedUsers.length > 0 && (
                                <button
                                    onClick={handleAddMembers}
                                    disabled={loading}
                                    className="mt-2 w-full px-3 py-1.5 rounded text-xs font-rasa bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50"
                                >
                                    {loading ? 'Adding...' : `Add ${selectedUsers.length} member(s)`}
                                </button>
                            )}
                        </div>
                    )}

                    <div className="space-y-1">
                        {conversation.members?.map(member => (
                            <div
                                key={member.id}
                                className={`group flex items-center gap-2 px-2 py-1.5 rounded-lg ${
                                    theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                                }`}
                            >
                                {member.avatar_url ? (
                                    <img src={member.avatar_url} alt="" className="w-7 h-7 rounded-full" />
                                ) : (
                                    <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs">
                                        {(member.first_name || member.username)?.charAt(0)?.toUpperCase()}
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-unna truncate">
                                        {member.first_name} {member.last_name}
                                        {member.id === currentUser?.id && (
                                            <span className={`text-xs font-rasa ml-1 ${
                                                theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                                            }`}>(you)</span>
                                        )}
                                    </p>
                                    {member.role === 'admin' && (
                                        <span className={`text-xs font-rasa ${
                                            theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                                        }`}>Admin</span>
                                    )}
                                </div>
                                {isAdmin && isGroup && member.id !== currentUser?.id && (
                                    <button
                                        onClick={() => handleRemoveMember(member.id)}
                                        className={`p-1 rounded text-red-400 hover:text-red-500 ${
                                            theme === 'dark' ? 'hover:bg-red-900/20' : 'hover:bg-red-50'
                                        } opacity-0 group-hover:opacity-100`}
                                        title="Remove member"
                                    >
                                        <UserMinus size={14} />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
        </div>
    );
}
